import PropTypes from "prop-types";
import { useState } from "react";
import { Download } from "lucide-react";
import { downloadChatMedia } from "../../lib/redux/actions";
import toast from "../../lib/utils/toast";

export default function MeetingTranscriptFileCard({ mediaItem, fileIndex, index, chatId }) {
  const fileUrl = mediaItem.previewUrl || mediaItem.fileUrl;
  const fileName = mediaItem.fileName
    || decodeURIComponent(fileUrl.split("/").pop().split("?")[0]);

  const fileExtension = fileName.includes(".")
    ? fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase()
    : null;

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    const mediaUrl = mediaItem.fileUrl || mediaItem.previewUrl;

    if (!chatId || !mediaUrl) {
      toast.error("File not available for download");
      return;
    }

    try {
      setIsDownloading(true);
      const blob = await downloadChatMedia({ chatId, mediaUrl });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      let errorMessage = "Download failed. Please try again.";

      if (error instanceof Blob) {
        try {
          const text = await error.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed?.message || errorMessage;
        } catch {
          // keep default message
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      key={`file-${index}-${fileIndex}`}
      disabled={isDownloading}
      onClick={handleDownload}
      type="button"
      className="bg-[#f0f0f0] hover:bg-[#e0e0e0] rounded px-3 py-2 text-sm cursor-pointer transition-colors w-full text-left"
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex-shrink-0">
          <img
            width={50}
            height={50}
            src={fileExtension ? `/extentionIcons/${fileExtension}.svg` : `/extentionIcons/file.png`}
            alt="file icon"
          />
        </div>
        <span className="max-w-[12vw] truncate">{fileName}</span>

        <div className="flex-shrink-0">
          {isDownloading ? (
            <svg
              className="animate-spin h-4 w-4 text-[#1B5C29] shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-label="Downloading"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <Download className="w-4 h-4 text-[#1B5C29]" />
          )}
        </div>
      </div>
    </button>
  );
}

MeetingTranscriptFileCard.propTypes = {
  mediaItem: PropTypes.object.isRequired,
  fileIndex: PropTypes.number,
  index: PropTypes.number,
  chatId: PropTypes.string,
};
